import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Form,
  Input,
  Col,
  Row,
  Flex,
  Select,
  InputNumber,
} from "antd";
import * as adminactions from "store/actions/AuthActions";
import { CKEditor } from "ckeditor4-react";
import axios from "axios";
import { compactToolbar } from "utilities/config";
import { nextLineKey } from "utilities/quoteLines";
import { DISCOUNT_TYPE_OPTIONS, discountedLineTotal } from "utilities/discount";

const InvoiceLineForm = ({
  invoiceId,
  invoiceItemData,
  setInvoiceItemData,
  taxRates,
  instance,
  form,
  reload,
}) => {
  const [products, setProducts] = useState([]);
  const [unit, setUnit] = useState("UNITS");
  const [taxRate1, setTaxRate1] = useState(0);
  const [taxRate2, setTaxRate2] = useState(0);
  const [taxRate3, setTaxRate3] = useState(0);
  const [loading, setLoading] = useState(false);

  // CKEditor is rebuilt whenever it gets a new `config` object, so the config has to
  // keep a stable identity across renders. Content is pushed through the editor
  // instance instead of `initData`, which is only read once on mount.
  const editorRef = useRef(null);
  const descriptionRef = useRef("<p>Add product Description!</p>");

  // Fix double-slash issue: REACT_APP_BASE_URL ends with '/'
  const baseUrl = (process.env.REACT_APP_BASE_URL || '').replace(/\/$/, '');
  const editorUrl = `${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/ckeditor.js`;
  const editorConfig = useMemo(() => ({
    extraPlugins: ["tableresize", "uploadimage", "createpdf"],
    removePlugins: [
      "exportpdf",
      "forms",
      "smiley",
      "language",
      "iframe",
      "about",
      "save",
      "preview",
      "print",
    ],
    filebrowserImageUploadUrl: `${baseUrl}/template/upload_template_image/`,
    fileTools_requestHeaders: axios.defaults.headers.common,
    uploadUrl: `${baseUrl}/template/upload_template_image/`,
    contentsCss: [`${baseUrl}/staticfiles/ckeditor/4.19.0/full-all/document-style.css`, "/ckeditor-content.css"],
    bodyClass: "document-editor",
    height: "5cm",
    allowedContent: true,
    protectedSource: [/{%[\s\S]*?%}+/g, /{{[\s\S]*?}}+/g],
    // Phones get a short toolbar; desktop keeps the full grouped one.
    ...compactToolbar(),
  }), [baseUrl]);

  const setEditorContent = (html) => {
    descriptionRef.current = html || "";
    if (editorRef.current) {
      editorRef.current.setData(descriptionRef.current);
    }
  };

  const loadProducts = async () => {
    const response = await adminactions.loadProducts();
    if (response?.data?.results) {
      setProducts(response.data.results);
    }
  };

  const rateOf = (taxRateId) => {
    const taxRate = taxRates?.find((item) => item.id === taxRateId);
    return taxRate ? taxRate.rate / 100 : 0;
  };

  // Copies every detail of a saved product onto this line, so picking one from the
  // list behaves like a quick product instead of just filling in a title and price.
  const applyProduct = (productId) => {
    const product = products.find((item) => item.id === productId);
    if (!product) {
      return;
    }

    const productDescription = product.description || product.notes || "";
    const productUnit = product.unit || "UNITS";
    const quantity = productUnit === "FIXED" ? 1 : (product.quantity ?? 1);
    const price = product.price ?? 0;
    const rate1 = rateOf(product.tax_rate1);
    const rate2 = rateOf(product.tax_rate2);
    const rate3 = rateOf(product.tax_rate3);

    setEditorContent(productDescription);
    setUnit(productUnit);
    setTaxRate1(rate1);
    setTaxRate2(rate2);
    setTaxRate3(rate3);

    form.setFieldsValue({
      title: product.title,
      description: productDescription,
      hsn: product.hsn,
      unit: productUnit,
      price,
      quantity,
      is_fixed_rate: productUnit === "FIXED",
      tax_rate1: product.tax_rate1 || undefined,
      tax_rate2: product.tax_rate2 || undefined,
      tax_rate3: product.tax_rate3 || undefined,
      line_total: discountedLineTotal(
        quantity,
        price,
        form.getFieldValue("discount_type"),
        form.getFieldValue("discount_value"),
        [rate1, rate2, rate3]
      ),
    });
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      // `item` only drives the product picker; it is not part of the line item.
      const { item, ...lineValues } = values;
      const payload = {
        ...lineValues,
        discount_type: lineValues.discount_type || null,
        discount_value: lineValues.discount_type ? lineValues.discount_value || 0 : 0,
        is_fixed_rate: lineValues.unit === "FIXED",
        invoice: invoiceId,
        item_type: "PRODUCT",
      };

      if (instance) {
        // Rows are matched on `key`, the identity the table and drag-and-drop
        // use. Matching on `id` collapsed every row whose id was still
        // undefined — a clone, say — into this one.
        setInvoiceItemData((prevData) =>
          prevData.map((prod) =>
            prod.key === instance.key
              ? // `prod` spreads first so the saved line keeps its id and the
                // backend updates it instead of replacing it.
                { ...prod, ...payload, id: prod.id, key: prod.key }
              : prod
          )
        );
        reload();
      } else {
        setInvoiceItemData((invoiceItemData) => [
          ...invoiceItemData,
          {
            ...payload,
            key: nextLineKey(invoiceItemData),
          },
        ]);
        reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const onChange = (tax_rate_1 = 0, tax_rate_2 = 0, tax_rate_3 = 0) => {
    setTaxRate1(tax_rate_1);
    setTaxRate2(tax_rate_2);
    setTaxRate3(tax_rate_3);
    form.setFieldValue(
      "line_total",
      discountedLineTotal(
        form.getFieldValue("quantity"),
        form.getFieldValue("price"),
        form.getFieldValue("discount_type"),
        form.getFieldValue("discount_value"),
        [tax_rate_1, tax_rate_2, tax_rate_3]
      )
    );
  };

  useEffect(() => {
    if (instance) {
      setEditorContent(instance.description);
      setUnit(instance.unit || "UNITS");
      setTaxRate1(rateOf(instance.tax_rate1));
      setTaxRate2(rateOf(instance.tax_rate2));
      setTaxRate3(rateOf(instance.tax_rate3));
      form.setFieldsValue({
        item: undefined,
        ...instance,
        discount_type: instance.discount_type || undefined,
        discount_value: instance.discount_value || undefined,
      });
    } else {
      // Reset to default when switching to add mode
      setEditorContent("<p>Add product Description!</p>");
      setUnit("UNITS");
      setTaxRate1(0);
      setTaxRate2(0);
      setTaxRate3(0);
      form.setFieldsValue({
        item: undefined,
        quantity: 1,
        discount_type: undefined,
        discount_value: undefined,
      });
    }
  }, [instance]);

  return (
    <Form
      form={form}
      labelCol={{ span: 5 }}
      wrapperCol={{ span: 18 }}
      onFinish={onFinish}
      initialValues={{
        quantity: 1
      }}
    >
      <Row style={{ margin: "15px" }}>
        <Col span={24}>
          <div className="site-card-border-less-wrapper">
            <div>
              <Form.Item label="Item" name="item">
                <Select
                  showSearch
                  placeholder="Select Product"
                  optionFilterProp="children"
                  allowClear
                  onChange={(value) => applyProduct(value)}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={products.map((item) => {
                    return { value: item.id, label: item.title };
                  })}
                />
              </Form.Item>
              <Form.Item
                label="Title"
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please Input title!`,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Description"
                name="description"
                getValueFromEvent={(event) => {
                  const data = event.editor.getData();
                  return data;
                }}
              >
                <CKEditor
                  initData=""
                  config={editorConfig}
                  editorUrl={editorUrl}
                  onInstanceReady={({ editor }) => {
                    editorRef.current = editor;
                    editor.setData(descriptionRef.current);
                  }}
                  onDestroy={() => {
                    editorRef.current = null;
                  }}
                />
              </Form.Item>
              <Form.Item label="HSN/SAC" name="hsn">
                <Input />
              </Form.Item>
              <Form.Item
                label="Unit"
                name="unit"
                rules={[{ required: true, message: "Please select a unit!" }]}
              >
                <Select
                  options={[
                    { value: "DAYS", label: "DAYS" },
                    { value: "HOURS", label: "HOURS" },
                    { value: "FIXED", label: "FIXED" },
                    { value: "UNITS", label: "UNITS" },
                  ]}
                  onChange={(value) => {
                    setUnit(value);
                    if (value === "FIXED") {
                      form.setFieldValue("quantity", 1);
                    }
                    onChange(taxRate1, taxRate2, taxRate3);
                  }}
                />
              </Form.Item>
              <Form.Item label="Price/unit" type="number" name="price">
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  onChange={(value) => {
                    if (value) {
                      onChange(taxRate1, taxRate2, taxRate3);
                      // const quantity = form.getFieldValue("quantity");
                      // const price = form.getFieldValue("price");
                      // form.setFieldValue("line_total", quantity * price);
                    }
                  }}
                />
              </Form.Item>
              <Form.Item label="Quantity" type="number" name="quantity">
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  onChange={(value) => {
                    if (value) {
                      onChange(taxRate1, taxRate2, taxRate3);
                    }
                  }}
                  disabled={unit === "FIXED"}
                />
              </Form.Item>
              <Form.Item label="Discount Type" name="discount_type">
                <Select
                  placeholder="No discount"
                  allowClear
                  options={DISCOUNT_TYPE_OPTIONS}
                  onChange={(value) => {
                    if (!value) {
                      form.setFieldValue("discount_value", undefined);
                    }
                    onChange(taxRate1, taxRate2, taxRate3);
                  }}
                />
              </Form.Item>
              <Form.Item noStyle dependencies={["discount_type"]}>
                {({ getFieldValue }) =>
                  getFieldValue("discount_type") ? (
                    <Form.Item
                      label="Discount"
                      name="discount_value"
                      rules={[
                        { required: true, message: "Please enter the discount!" },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        max={getFieldValue("discount_type") === "PERCENT" ? 100 : undefined}
                        step={0.01}
                        precision={2}
                        addonAfter={getFieldValue("discount_type") === "PERCENT" ? "%" : undefined}
                        onChange={() => onChange(taxRate1, taxRate2, taxRate3)}
                      />
                    </Form.Item>
                  ) : null
                }
              </Form.Item>
              <Form.Item label="Tax Rate 1" name="tax_rate1">
                <Select
                  showSearch
                  placeholder="Select Tax Rate"
                  allowClear
                  onChange={(value) => {
                    const taxRate = taxRates.find((item) => item.id === value);
                    if (taxRate) {
                      onChange(taxRate.rate / 100, taxRate2, taxRate3);
                    } else {
                      onChange(0, taxRate2, taxRate3);
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={taxRates?.map((item, index) => {
                    return {
                      value: item.id,
                      label: `${item.title + " " + item.rate} %`,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item label="Tax Rate 2" name="tax_rate2">
                <Select
                  showSearch
                  placeholder="Select Tax Rate"
                  allowClear
                  onChange={(value) => {
                    const taxRate = taxRates.find((item) => item.id === value);
                    if (taxRate) {
                      onChange(taxRate1, taxRate.rate / 100, taxRate3);
                    } else {
                      onChange(taxRate1, 0, taxRate3);
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={taxRates?.map((item, index) => {
                    return {
                      value: item.id,
                      label: `${item.title + " " + item.rate} %`,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item label="Tax Rate 3" name="tax_rate3">
                <Select
                  showSearch
                  placeholder="Select Tax Rate"
                  allowClear
                  onChange={(value) => {
                    const taxRate = taxRates.find((item) => item.id === value);
                    if (taxRate) {
                      onChange(taxRate1, taxRate2, taxRate.rate / 100);
                    } else {
                      onChange(taxRate1, taxRate2, 0);
                    }
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={taxRates?.map((item, index) => {
                    return {
                      value: item.id,
                      label: `${item.title + " " + item.rate} %`,
                    };
                  })}
                />
              </Form.Item>
              <Form.Item label="Line Total" name="line_total">
                <Input disabled />
              </Form.Item>
            </div>
            <Flex justify="flex-end">
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ maxWidth: 100 }}
              >
                Save
              </Button>
            </Flex>
          </div>
        </Col>
      </Row>
    </Form>
  );
};

export default InvoiceLineForm;
